import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Cloak redirect handler · eigooinc.net/go/:slug
 *
 * Director directive 2026.02 (bugfix): slug→URL resolution is now
 * OWNED by the frontend via the AFFILIATE_TARGETS map below. This is
 * the single source of truth — no env var drift, no backend dependency
 * for the actual redirect. The backend is only used (best-effort) for
 * click telemetry.
 *
 * Flow:
 *  1. Look up affiliate URL in AFFILIATE_TARGETS[slug].
 *  2. Fire fire-and-forget click telemetry to /api/partners/click/:slug.
 *  3. Hard-redirect via window.location.replace.
 *
 * Unknown slug → render "not in roster" + return-to-hub CTA.
 */

// Slug → actual affiliate URL. THIS is the single source of truth.
// Update this map (and redeploy) whenever a partner's tracking link rotates.
const AFFILIATE_TARGETS = {
    protonvpn: "https://protonvpn.com/?ref=2798569",
    exness: "https://www.exnessaffiliates.com",
    draftkings: "https://www.draftkings.com/affiliates",
    vantage: "https://www.vantagemarkets.com/partnerships/affiliate/",
    samsung: "https://www.samsung.com/",
    "agent-z-rig": "https://www.amazon.com/raspberry-pi-5?tag=eigoo0fa-20",
};

export default function PartnerGo() {
    const { slug } = useParams();
    const [status, setStatus] = useState("resolving");
    const [error, setError] = useState(null);
    const [destination, setDestination] = useState(null);

    useEffect(() => {
        const dest = AFFILIATE_TARGETS[slug];
        if (!dest) {
            setError("Signal not in roster.");
            setStatus("error");
            return undefined;
        }
        setDestination(dest);
        // Fire-and-forget telemetry — never blocks the redirect, never
        // influences the destination URL.
        try {
            axios.post(`${BACKEND}/api/partners/click/${slug}`).catch(() => {});
        } catch {
            /* swallow */
        }
        setStatus("redirecting");
        // Short delay so the cloak frame paints once.
        const t = setTimeout(() => {
            window.location.replace(dest);
        }, 350);
        return () => clearTimeout(t);
    }, [slug]);

    return (
        <div
            data-testid="partner-go-frame"
            className="min-h-screen flex items-center justify-center px-6 text-white"
            style={{
                background: "radial-gradient(ellipse at center, #0D1824 0%, #050A0F 70%, #02060A 100%)",
                fontFamily: "'Inter', sans-serif",
            }}
        >
            <div className="max-w-lg w-full text-center">
                <div
                    className="text-[11px] uppercase tracking-[0.4em] mb-6"
                    style={{
                        color: "#00E5FF",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                    data-testid="partner-go-overline"
                >
                    ◢ Sovereign Cloak Routing
                </div>
                <h1
                    className="text-3xl md:text-4xl uppercase font-black tracking-tight"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                    data-testid="partner-go-title"
                >
                    {status === "error" ? "Routing Failure" : "Patching Signal"}
                </h1>
                <p
                    className="mt-4 text-base leading-relaxed"
                    style={{ color: "#829AB1" }}
                    data-testid="partner-go-status"
                >
                    {status === "resolving" && "Acquiring affiliate vector…"}
                    {status === "redirecting" && (
                        <>
                            Handing off to{" "}
                            <span style={{ color: "#E8C56C", fontWeight: 700 }}>{slug}</span> ·
                            stand by.
                        </>
                    )}
                    {status === "error" && (error || "Unknown error.")}
                </p>

                {status === "redirecting" && destination && (
                    <div
                        className="mt-10 inline-block px-4 py-2 text-[11px] uppercase tracking-[0.3em]"
                        style={{
                            border: "1px solid #1E3654",
                            color: "#5C7894",
                            fontFamily: "'Montserrat', sans-serif",
                        }}
                    >
                        <a
                            href={destination}
                            rel="noopener noreferrer sponsored"
                            style={{ color: "#E8C56C" }}
                            data-testid="partner-go-manual"
                        >
                            Tap if redirect stalls ↗
                        </a>
                    </div>
                )}

                {status === "error" && (
                    <Link
                        to="/partners"
                        data-testid="partner-go-back"
                        className="mt-10 inline-block px-5 py-3 uppercase tracking-[0.25em] text-xs font-bold"
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: "#E8C56C",
                            border: "1px solid #E8C56C",
                        }}
                    >
                        [ RETURN TO HUB ]
                    </Link>
                )}

                {/* Pulse */}
                {status !== "error" && (
                    <div className="mt-12 flex items-center justify-center gap-2">
                        <span
                            className="inline-block w-2.5 h-2.5 rounded-full animate-pulse"
                            style={{ background: "#00E5FF", boxShadow: "0 0 12px rgba(0,229,255,0.7)" }}
                        />
                        <span
                            className="text-[10px] uppercase tracking-[0.4em]"
                            style={{ color: "#829AB1", fontFamily: "'Montserrat', sans-serif" }}
                        >
                            Cloak Active
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
